import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, formatCurrency, formatPercent, formatDays, calculateDelta } from '../utils/formatters';
import ReportsTab from './ReportsTab';
import DisputesTab from './DisputesTab';

// Delta Indicator Component - shows change from previous period
const DeltaIndicator = ({ current, previous, format = 'number', inverted = false }) => {
  const delta = calculateDelta(current, previous, format);

  if (delta.isPositive === null) {
    return <span className="text-xs text-gray-400">{delta.formatted}</span>;
  }

  // For some metrics (like costs), negative is good - use inverted prop
  const isGood = inverted ? !delta.isPositive : delta.isPositive;

  return (
    <span className={`text-xs ${isGood ? 'text-green-600' : 'text-red-600'}`}>
      {delta.formatted}
    </span>
  );
};

// Authenticated fetch helper — checks for 401 and triggers logout
const authFetch = async (url, logoutFn, options = {}) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  });
  if (response.status === 401) {
    logoutFn();
    throw new Error('Session expired');
  }
  return response;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [watchLists, setWatchLists] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [dashboardRes, watchListsRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/dashboard`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/watch-lists`, logout)
      ]);

      if (!dashboardRes.ok || !watchListsRes.ok) {
        throw new Error('Failed to fetch admin data');
      }

      const [dashboard, watchListsData] = await Promise.all([
        dashboardRes.json(),
        watchListsRes.json()
      ]);

      setDashboardData(dashboard);
      setWatchLists(watchListsData);
    } catch (err) {
      if (err.message !== 'Session expired') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">RideHowl Admin</h1>
            <p className="text-indigo-200 mt-1">System management dashboard</p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-indigo-200">{user?.email}</span>
            <button
              onClick={logout}
              className="bg-indigo-800 hover:bg-indigo-900 px-4 py-2 rounded text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {['dashboard', 'financials', 'payouts', 'watch-lists', 'reports', 'disputes', 'system', 'drivers', 'riders', 'transactions'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'dashboard' && <DashboardTab data={dashboardData} setActiveTab={setActiveTab} />}
        {activeTab === 'watch-lists' && <WatchListsTab data={watchLists} onRefresh={fetchData} />}
        {activeTab === 'transactions' && <TransactionsTab />}
        {activeTab === 'drivers' && <DriversTab />}
        {activeTab === 'riders' && <RidersTab />}
        {activeTab === 'payouts' && <PayoutsTab />}
        {activeTab === 'financials' && <FinancialsTab />}
        {activeTab === 'reports' && <ReportsTab />}
        {activeTab === 'disputes' && <DisputesTab />}
        {activeTab === 'system' && <SystemTab />}
      </div>
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab = ({ data, setActiveTab }) => {
  const { logout } = useAuth();
  const [stripeBalance, setStripeBalance] = useState(null);
  const [subscriptionMetrics, setSubscriptionMetrics] = useState(null);
  const [ytdFinancials, setYtdFinancials] = useState(null);
  const [dailySignups, setDailySignups] = useState(null);

  useEffect(() => {
    fetchExtendedMetrics();
  }, []);

  const fetchExtendedMetrics = async () => {
    try {
      const [balanceRes, subRes, ytdRes, signupsRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/stripe-balance`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/subscription-metrics?period=month`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/financials?period=year`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/daily-signups`, logout)
      ]);

      if (balanceRes.ok) setStripeBalance(await balanceRes.json());
      if (subRes.ok) setSubscriptionMetrics(await subRes.json());
      if (ytdRes.ok) setYtdFinancials(await ytdRes.json());
      if (signupsRes.ok) setDailySignups(await signupsRes.json());
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching extended metrics:', err);
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Today's Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Active Rides" value={formatNumber(data.today.active_rides)} color="blue" />
        <StatCard title="Completed Today" value={formatNumber(data.today.completed_rides)} color="green" />
        <div
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('financials')}
        >
          <div className="text-sm text-gray-500">Revenue Today</div>
          <div className="text-3xl font-bold text-indigo-600">{formatCurrency(data.today.revenue)}</div>
          <div className="text-xs text-indigo-400 mt-1">Click for details →</div>
        </div>
        <div
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('financials')}
        >
          <div className="text-sm text-gray-500">YTD Revenue</div>
          <div className="text-3xl font-bold text-green-600">
            {ytdFinancials ? formatCurrency(ytdFinancials.summary.platform_fees) : '—'}
          </div>
          <div className="text-xs text-green-400 mt-1">Click for details →</div>
        </div>
      </div>

      {/* Subscription & Stripe Balance Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">MRR</div>
          <div className="text-2xl font-bold text-purple-600">
            {subscriptionMetrics ? formatCurrency(subscriptionMetrics.mrr) : '—'}
          </div>
          <div className="text-xs text-gray-400">Monthly Recurring Revenue</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">ARR</div>
          <div className="text-2xl font-bold text-purple-600">
            {subscriptionMetrics ? formatCurrency(subscriptionMetrics.arr) : '—'}
          </div>
          <div className="text-xs text-gray-400">Annual Recurring Revenue</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Driver Churn (Month)</div>
          <div className="text-2xl font-bold text-orange-600">
            {subscriptionMetrics ? formatPercent(subscriptionMetrics.churn.churn_rate, 1) : '—'}
          </div>
          <div className="text-xs text-gray-400">
            {subscriptionMetrics ? `${subscriptionMetrics.churn.churned_count} churned` : ''}
          </div>
        </div>
        <div
          className={`bg-white rounded-lg shadow p-6 ${stripeBalance?.warning ? 'border-2 border-red-500' : ''}`}
        >
          <div className="text-sm text-gray-500">Stripe Balance</div>
          <div className={`text-2xl font-bold ${stripeBalance?.warning ? 'text-red-600' : 'text-gray-900'}`}>
            {stripeBalance ? formatCurrency(stripeBalance.available_balance) : '—'}
          </div>
          {stripeBalance?.warning && (
            <div className="text-xs text-red-600 font-medium mt-1">
              Warning: Projected payouts exceed balance!
            </div>
          )}
          {!stripeBalance?.warning && stripeBalance && (
            <div className="text-xs text-gray-400">
              Pending: {formatCurrency(stripeBalance.pending_balance)}
            </div>
          )}
        </div>
      </div>

      {/* Daily Signups & Reports Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Driver Signups</div>
          <div className="text-2xl font-bold text-gray-900">
            {dailySignups ? formatNumber(dailySignups.new_drivers_today) : '—'}
          </div>
          <div className="text-xs text-gray-400">Today</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Subscriptions</div>
          <div className="text-2xl font-bold text-purple-600">
            {dailySignups ? formatNumber(dailySignups.new_subscriptions_today?.total || 0) : '—'}
          </div>
          <div className="text-xs text-gray-400">
            {dailySignups?.new_subscriptions_today?.by_tier
              ? `Cpt: ${dailySignups.new_subscriptions_today.by_tier.Captain || 0}, Nvg: ${dailySignups.new_subscriptions_today.by_tier.Navigator || 0}, Cru: ${dailySignups.new_subscriptions_today.by_tier.Cruiser || 0}, Com: ${dailySignups.new_subscriptions_today.by_tier.Commuter || 0}`
              : 'Paid tiers only'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Rider Signups</div>
          <div className="text-2xl font-bold text-gray-900">
            {dailySignups ? formatNumber(dailySignups.new_riders_today) : '—'}
          </div>
          <div className="text-xs text-gray-400">Today</div>
        </div>
        <div
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setActiveTab('reports')}
        >
          <div className="text-sm text-gray-500">Outstanding Reports</div>
          <div className="text-2xl font-bold text-red-600">
            {dailySignups ? formatNumber(dailySignups.outstanding_reports) : '—'}
          </div>
          <div className="text-xs text-indigo-400 mt-1">Click to view →</div>
        </div>
      </div>

      {/* Settlement Pipeline - Clickable */}
      <div
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setActiveTab('payouts')}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Settlement Pipeline</h3>
          <span className="text-xs text-indigo-400">Click for payouts →</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PipelineStep label="Pending Capture" count={formatNumber(data.settlement_pipeline.pending_capture)} />
          <PipelineStep label="Awaiting Settlement" count={formatNumber(data.settlement_pipeline.awaiting_settlement)} />
          <PipelineStep label="Ready for Payout" count={formatNumber(data.settlement_pipeline.ready_for_payout)} />
        </div>
      </div>

      {/* Payout Queue - Clickable */}
      <div
        className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setActiveTab('payouts')}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Payout Queue</h3>
          <span className="text-xs text-indigo-400">Click for payouts →</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded">
            <div className="text-2xl font-bold text-green-600">{formatNumber(data.payout_queue.ready)}</div>
            <div className="text-sm text-gray-600">Ready</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded">
            <div className="text-2xl font-bold text-yellow-600">{formatNumber(data.payout_queue.blocked)}</div>
            <div className="text-sm text-gray-600">Blocked</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded">
            <div className="text-2xl font-bold text-red-600">{formatNumber(data.payout_queue.failed)}</div>
            <div className="text-sm text-gray-600">Failed</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded">
            <div className="text-2xl font-bold text-blue-600">
              {stripeBalance ? formatCurrency(stripeBalance.projected_payouts_tonight) : '—'}
            </div>
            <div className="text-sm text-gray-600">Est. Tonight</div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {data.alerts && data.alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-4">Alerts</h3>
          <div className="space-y-2">
            {data.alerts.map((alert, idx) => (
              <div key={idx} className="flex items-center space-x-2 text-red-700">
                <span className="font-medium">{alert.type}:</span>
                <span>{alert.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Watch Lists Tab Component
const WatchListsTab = ({ data, onRefresh }) => {
  const { logout } = useAuth();
  const [forceCompleting, setForceCompleting] = useState(null);
  const [forcePaying, setForcePaying] = useState(null);

  const handleForceComplete = async (rideId) => {
    if (!window.confirm(`Force complete ride ${rideId}?`)) return;

    setForceCompleting(rideId);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/rides/${rideId}/force-complete`,
        logout,
        { method: 'POST' }
      );
      if (response.ok) {
        alert('Ride force-completed successfully');
        onRefresh();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to force complete'}`);
      }
    } catch (err) {
      if (err.message !== 'Session expired') alert(`Error: ${err.message}`);
    } finally {
      setForceCompleting(null);
    }
  };

  const handleForcePayout = async (rideId) => {
    if (!window.confirm(`Force payout for ride ${rideId}?`)) return;

    setForcePaying(rideId);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/rides/${rideId}/force-payout`,
        logout,
        { method: 'POST' }
      );
      if (response.ok) {
        alert('Payout initiated successfully');
        onRefresh();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to initiate payout'}`);
      }
    } catch (err) {
      if (err.message !== 'Session expired') alert(`Error: ${err.message}`);
    } finally {
      setForcePaying(null);
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-8">
      {/* Overdue Completions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-red-600">
            Overdue Completions ({formatNumber(data.overdue_completions.count)})
          </h3>
          <p className="text-sm text-gray-500">Rides with pickup over 2 days ago that haven't been completed</p>
        </div>
        {data.overdue_completions.items.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No overdue completions</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ride ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pickup</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.overdue_completions.items.map(item => (
                  <tr key={`${item.type}-${item.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{item.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.pickup_datetime).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{formatDays(item.days_overdue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleForceComplete(item.ride_id)}
                        disabled={forceCompleting === item.ride_id}
                        className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                      >
                        {forceCompleting === item.ride_id ? 'Processing...' : 'Force Complete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Overdue Payouts */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-orange-600">
            Overdue Payouts ({formatNumber(data.overdue_payouts.count)})
          </h3>
          <p className="text-sm text-gray-500">Completed rides over 4 days ago that haven't been paid out</p>
        </div>
        {data.overdue_payouts.items.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No overdue payouts</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ride ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocking Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.overdue_payouts.items.map(item => (
                  <tr key={`${item.type}-${item.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{item.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.completed_datetime).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">{item.blocking_reason}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleForcePayout(item.ride_id)}
                        disabled={forcePaying === item.ride_id}
                        className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                      >
                        {forcePaying === item.ride_id ? 'Processing...' : 'Force Payout'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Transactions Tab Component
const TransactionsTab = () => {
  const { logout } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    transactionType: '',
    limit: 50
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.transactionType) params.append('transaction_type', filters.transactionType);
      params.append('limit', filters.limit);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/transactions?${params}`,
        logout
      );
      const data = await response.json();
      setTransactions(data.transactions || []);
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="date"
            value={filters.startDate}
            onChange={e => setFilters({ ...filters, startDate: e.target.value })}
            className="border rounded px-3 py-2"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={e => setFilters({ ...filters, endDate: e.target.value })}
            className="border rounded px-3 py-2"
            placeholder="End Date"
          />
          <select
            value={filters.transactionType}
            onChange={e => setFilters({ ...filters, transactionType: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Types</option>
            <option value="ride">Rides</option>
            <option value="day_booking">Day Bookings</option>
          </select>
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="captured">Captured</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={fetchTransactions}
            className="bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading transactions...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fare</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Fee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Refund</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map(tx => (
                  <tr key={`${tx.type}-${tx.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{tx.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        tx.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {tx.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.rider_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(tx.fare)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(tx.tip || 0)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">{formatCurrency(tx.platform_fee)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {tx.refund_amount ? (
                        <span title={`${formatPercent(tx.refund_percentage || 0)} of fare`}>
                          {formatCurrency(tx.refund_amount)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        (tx.display_status || tx.capture_status) === 'captured' ? 'bg-green-100 text-green-800' :
                        (tx.display_status || tx.capture_status) === 'failed' ? 'bg-red-100 text-red-800' :
                        (tx.display_status || tx.capture_status) === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {tx.display_status || tx.capture_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Drivers Tab Component
const DriversTab = () => {
  const { logout } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [cohortFormat, setCohortFormat] = useState('year-month'); // year, year-month, year-month-day

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', 100);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/drivers?${params}`,
        logout
      );
      const data = await response.json();
      setDrivers(data.drivers || []);
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDriverToggle = async (driverId, isActive, driverName) => {
    const action = isActive ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this Driver?`)) return;
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/drivers/${driverId}/${action}`,
        logout,
        { method: 'POST' }
      );
      if (response.ok) {
        fetchDrivers();
      } else {
        const err = await response.json();
        alert(`Failed to ${action}: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      if (err.message !== 'Session expired') alert(`Failed to ${action} driver`);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedDrivers = [...drivers].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal = a[key];
    let bVal = b[key];

    // Handle null values
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    // Numeric sort for numbers
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    // String sort
    const comparison = String(aVal).localeCompare(String(bVal));
    return direction === 'asc' ? comparison : -comparison;
  });

  const formatCohort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (cohortFormat === 'year') {
      return date.getFullYear().toString();
    } else if (cohortFormat === 'year-month') {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] border rounded px-3 py-2"
            placeholder="Search by name..."
          />
          <button
            onClick={fetchDrivers}
            className="bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700"
          >
            Search
          </button>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Cohort:</span>
            <select
              value={cohortFormat}
              onChange={e => setCohortFormat(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="year">Year</option>
              <option value="year-month">Year-Month</option>
              <option value="year-month-day">Full Date</option>
            </select>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading drivers...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader label="Driver" sortKey="name" />
                  <SortHeader label="Tier" sortKey="tier" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <SortHeader label="Driver Earnings" sortKey="total_earnings" />
                  <SortHeader label="Pending" sortKey="pending_payout" />
                  <SortHeader label="Platform Earnings" sortKey="total_platform_earnings" />
                  <SortHeader label="Platform Fees" sortKey="platform_fee_revenue" />
                  <SortHeader label="Sub Revenue" sortKey="subscription_revenue" />
                  <SortHeader label="Rides" sortKey="completed_rides" />
                  <SortHeader label="Cohort" sortKey="created_at" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedDrivers.map(driver => (
                  <tr key={driver.driver_id} className={!driver.is_active ? 'bg-gray-50' : ''}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                      <div className="text-xs text-gray-500">{driver.email}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{driver.tier}</span>
                      <div className="text-xs text-gray-400">{formatPercent(driver.commission_rate, 0)} fee</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          driver.stripe_onboarding_complete ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {driver.stripe_onboarding_complete ? 'Connect OK' : 'Connect Pending'}
                        </span>
                        {!driver.is_active && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(driver.total_earnings)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-600">{formatCurrency(driver.pending_payout)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(driver.total_platform_earnings || 0)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-indigo-600">{formatCurrency(driver.platform_fee_revenue)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600">{formatCurrency(driver.subscription_revenue || 0)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(driver.completed_rides)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatCohort(driver.created_at)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDriverToggle(driver.driver_id, driver.is_active, driver.name)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          driver.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {driver.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Riders Tab Component
const RidersTab = () => {
  const { logout } = useAuth();
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [cohortFormat, setCohortFormat] = useState('year-month');

  useEffect(() => {
    fetchRiders();
  }, []);

  const fetchRiders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', 100);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/riders?${params}`,
        logout
      );
      const data = await response.json();
      setRiders(data.riders || []);
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching riders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRiderToggle = async (riderId, isActive) => {
    const action = isActive ? 'deactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} this Rider?`)) return;
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/riders/${riderId}/${action}`,
        logout,
        { method: 'POST' }
      );
      if (response.ok) {
        fetchRiders();
      } else {
        const err = await response.json();
        alert(`Failed to ${action}: ${err.detail || 'Unknown error'}`);
      }
    } catch (err) {
      if (err.message !== 'Session expired') alert(`Failed to ${action} rider`);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedRiders = [...riders].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal = a[key];
    let bVal = b[key];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const comparison = String(aVal).localeCompare(String(bVal));
    return direction === 'asc' ? comparison : -comparison;
  });

  const formatCohort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (cohortFormat === 'year') return date.getFullYear().toString();
    if (cohortFormat === 'year-month') return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return date.toLocaleDateString();
  };

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] border rounded px-3 py-2"
            placeholder="Search by name..."
          />
          <button
            onClick={fetchRiders}
            className="bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700"
          >
            Search
          </button>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Cohort:</span>
            <select
              value={cohortFormat}
              onChange={e => setCohortFormat(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="year">Year</option>
              <option value="year-month">Year-Month</option>
              <option value="year-month-day">Full Date</option>
            </select>
          </div>
        </div>
      </div>

      {/* Riders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading riders...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortHeader label="Rider" sortKey="name" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <SortHeader label="Total Paid" sortKey="total_paid" />
                  <SortHeader label="Total Rides" sortKey="total_rides" />
                  <SortHeader label="Platform Earnings" sortKey="platform_earnings" />
                  <SortHeader label="Cohort" sortKey="created_at" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRiders.map(rider => (
                  <tr key={rider.rider_id} className={!rider.is_active ? 'bg-gray-50' : ''}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{rider.name}</div>
                      <div className="text-xs text-gray-500">{rider.email}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        rider.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rider.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(rider.total_paid)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(rider.total_rides)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(rider.platform_earnings)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatCohort(rider.created_at)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleRiderToggle(rider.rider_id, rider.is_active)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          rider.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {rider.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Payouts Tab Component
const PayoutsTab = () => {
  const { logout } = useAuth();
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [failedPayouts, setFailedPayouts] = useState([]);
  const [completedPayouts, setCompletedPayouts] = useState([]);
  const [completedMeta, setCompletedMeta] = useState({});
  const [stripeBalance, setStripeBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('pending');

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const [pendingRes, failedRes, completedRes, balanceRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/payouts/pending`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/payouts/failed`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/payouts/completed`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/stripe-balance`, logout)
      ]);

      const [pending, failed, completed, balance] = await Promise.all([
        pendingRes.json(),
        failedRes.json(),
        completedRes.json(),
        balanceRes.ok ? balanceRes.json() : null
      ]);

      setPendingPayouts(pending.pending_payouts || []);
      setFailedPayouts(failed.failed_payouts || []);
      setCompletedPayouts(completed.completed_payouts || []);
      setCompletedMeta({ avg_days_to_transfer: completed.avg_days_to_transfer });
      setStripeBalance(balance);
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching payouts:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`bg-white rounded-lg shadow p-4 ${stripeBalance?.warning ? 'border-2 border-red-500' : ''}`}>
          <div className="text-sm text-gray-500">Stripe Balance</div>
          <div className={`text-xl font-bold ${stripeBalance?.warning ? 'text-red-600' : 'text-gray-900'}`}>
            {stripeBalance ? formatCurrency(stripeBalance.available_balance) : '—'}
          </div>
          <div className="text-xs text-gray-400">
            Pending: {stripeBalance ? formatCurrency(stripeBalance.pending_balance) : '—'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Projected Tonight</div>
          <div className="text-xl font-bold text-blue-600">
            {stripeBalance ? formatCurrency(stripeBalance.projected_payouts_tonight) : '—'}
          </div>
          <div className="text-xs text-gray-400">{formatNumber(pendingPayouts.length)} rides ready</div>
        </div>
        <div className={`bg-white rounded-lg shadow p-4 ${stripeBalance?.warning ? 'bg-red-50' : ''}`}>
          <div className="text-sm text-gray-500">Net After Payouts</div>
          <div className={`text-xl font-bold ${stripeBalance?.net_after_payouts < 0 ? 'text-red-600' : 'text-green-600'}`}>
            {stripeBalance ? formatCurrency(stripeBalance.net_after_payouts) : '—'}
          </div>
          {stripeBalance?.warning && (
            <div className="text-xs text-red-600 font-medium">Insufficient funds!</div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Avg Days to Transfer</div>
          <div className="text-xl font-bold text-gray-900">
            {completedMeta.avg_days_to_transfer !== null ? formatDays(completedMeta.avg_days_to_transfer, 2) : '—'}
          </div>
          <div className="text-xs text-gray-400">Based on recent payouts</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-4">
        <button
          onClick={() => setActiveSubTab('pending')}
          className={`px-4 py-2 rounded ${activeSubTab === 'pending' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
        >
          Pending ({formatNumber(pendingPayouts.length)})
        </button>
        <button
          onClick={() => setActiveSubTab('failed')}
          className={`px-4 py-2 rounded ${activeSubTab === 'failed' ? 'bg-red-600 text-white' : 'bg-white text-gray-700'}`}
        >
          Failed ({formatNumber(failedPayouts.length)})
        </button>
        <button
          onClick={() => setActiveSubTab('completed')}
          className={`px-4 py-2 rounded ${activeSubTab === 'completed' ? 'bg-green-600 text-white' : 'bg-white text-gray-700'}`}
        >
          Completed ({formatNumber(completedPayouts.length)})
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading payouts...</div>
        ) : activeSubTab === 'pending' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ride ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eligible At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocking Reasons</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingPayouts.map(payout => (
                  <tr key={`${payout.type}-${payout.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{payout.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payout.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {payout.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payout.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payout.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.completion_date ? new Date(payout.completion_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.days_since_completion !== null ? formatNumber(payout.days_since_completion) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.eligible_at ? new Date(payout.eligible_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                      {payout.blocking_reasons?.join(', ') || 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeSubTab === 'failed' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ride ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {failedPayouts.map(payout => (
                  <tr key={`${payout.type}-${payout.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{payout.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payout.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {payout.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payout.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payout.amount)}</td>
                    <td className="px-6 py-4 text-sm text-red-600">{payout.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ride ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payout Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {completedPayouts.map(payout => (
                  <tr key={`${payout.type}-${payout.ride_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{payout.ride_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        payout.type === 'day_booking' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {payout.type === 'day_booking' ? 'Day Booking' : 'Ride'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payout.driver_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(payout.amount)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.completion_date ? new Date(payout.completion_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.payout_date ? new Date(payout.payout_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payout.days_to_transfer !== null ? formatNumber(payout.days_to_transfer) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono">
                      {payout.transfer_id || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Financials Tab Component
const FinancialsTab = () => {
  const { logout } = useAuth();
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchFinancials();
  }, [period]);

  const fetchFinancials = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/financials?period=${period}`,
        logout
      );
      const data = await response.json();
      setFinancials(data);
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching financials:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !financials) {
    return <div className="text-center text-gray-500 py-8">Loading financials...</div>;
  }

  const { summary } = financials;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center space-x-4">
        {['day', 'week', 'month', 'year', 'all'].map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded ${period === p ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            {p === 'all' ? 'All Time' : p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>

      {/* Signup Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Driver Signups</div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(financials.new_drivers || 0)}</div>
          <div className="text-xs text-gray-400">in period</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Subscriptions</div>
          <div className="text-2xl font-bold text-purple-600">{formatNumber(financials.new_subscriptions?.total || 0)}</div>
          <div className="text-xs text-gray-400">
            {financials.new_subscriptions?.by_tier
              ? `Cpt: ${financials.new_subscriptions.by_tier.Captain || 0}, Nvg: ${financials.new_subscriptions.by_tier.Navigator || 0}, Cru: ${financials.new_subscriptions.by_tier.Cruiser || 0}, Com: ${financials.new_subscriptions.by_tier.Commuter || 0}`
              : 'Paid tiers only'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">New Rider Signups</div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(financials.new_riders || 0)}</div>
          <div className="text-xs text-gray-400">in period</div>
        </div>
      </div>

      {/* Row 1: Platform Earnings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Platform Earnings</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_platform_earnings || 0)}</div>
          <DeltaIndicator current={summary.total_platform_earnings || 0} previous={financials.prev_summary?.total_platform_earnings || 0} format="currency" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Platform Fees</div>
          <div className="text-2xl font-bold text-indigo-600">{formatCurrency(summary.platform_fees)}</div>
          <DeltaIndicator current={summary.platform_fees} previous={financials.prev_summary?.platform_fees || 0} format="currency" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Subscription Revenue</div>
          <div className="text-2xl font-bold text-blue-600">{formatCurrency(summary.subscription_revenue || 0)}</div>
          <DeltaIndicator current={summary.subscription_revenue || 0} previous={financials.prev_summary?.subscription_revenue || 0} format="currency" />
        </div>
      </div>

      {/* Row 2: Volume Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Active Drivers</div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(summary.active_drivers)}</div>
          <DeltaIndicator current={summary.active_drivers} previous={financials.prev_summary?.active_drivers || 0} format="number" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Ride Count</div>
          <div className="text-2xl font-bold text-gray-900">{formatNumber(summary.ride_count)}</div>
          <DeltaIndicator current={summary.ride_count} previous={financials.prev_summary?.ride_count || 0} format="number" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Total Fare Volume</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_fare_volume)}</div>
          <DeltaIndicator current={summary.total_fare_volume} previous={financials.prev_summary?.total_fare_volume || 0} format="currency" />
        </div>
      </div>

      {/* Row 3: Per-Unit Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Platform Earnings per Driver</div>
          <div className="text-2xl font-bold text-gray-700">
            {summary.active_drivers > 0
              ? formatCurrency((summary.total_platform_earnings || 0) / summary.active_drivers)
              : '$0.00'}
          </div>
          <DeltaIndicator
            current={summary.active_drivers > 0 ? (summary.total_platform_earnings || 0) / summary.active_drivers : 0}
            previous={financials.prev_summary?.active_drivers > 0 ? (financials.prev_summary?.total_platform_earnings || 0) / financials.prev_summary.active_drivers : 0}
            format="currency"
          />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Platform Fees per Ride</div>
          <div className="text-2xl font-bold text-gray-700">
            {summary.ride_count > 0
              ? formatCurrency(summary.platform_fees / summary.ride_count)
              : '$0.00'}
          </div>
          <DeltaIndicator
            current={summary.ride_count > 0 ? summary.platform_fees / summary.ride_count : 0}
            previous={financials.prev_summary?.ride_count > 0 ? (financials.prev_summary?.platform_fees || 0) / financials.prev_summary.ride_count : 0}
            format="currency"
          />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-500">Platform Fees % of Volume</div>
          <div className="text-2xl font-bold text-gray-700">
            {summary.total_fare_volume > 0
              ? formatPercent((summary.platform_fees / summary.total_fare_volume) * 100, 1)
              : '0.0%'}
          </div>
          <DeltaIndicator
            current={summary.total_fare_volume > 0 ? (summary.platform_fees / summary.total_fare_volume) * 100 : 0}
            previous={financials.prev_summary?.total_fare_volume > 0 ? ((financials.prev_summary?.platform_fees || 0) / financials.prev_summary.total_fare_volume) * 100 : 0}
            format="percent"
          />
        </div>
      </div>

      {/* Tier Breakdown */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Revenue by Tier</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drivers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rides</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Earnings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Earnings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Fees</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Fees</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">% of Sub</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {financials.tier_breakdown?.map(tier => (
                <tr key={tier.tier_id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tier.tier_name}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{formatNumber(tier.driver_count || 0)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatPercent(tier.commission_rate, 0)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(tier.ride_count)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600">{formatCurrency(tier.total_platform_earnings || 0)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatPercent(tier.pct_of_total_earnings || 0, 1)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-indigo-600">{formatCurrency(tier.platform_fee_revenue)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatPercent(tier.pct_of_total_fees, 1)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600">{formatCurrency(tier.subscription_revenue || 0)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatPercent(tier.pct_of_total_subscription || 0, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// System Tab Component
const SystemTab = () => {
  const { logout } = useAuth();
  const [health, setHealth] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [healthRes, schedulerRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/system/health`, logout),
        authFetch(`${process.env.REACT_APP_API_URL}/api/admin/system/scheduler-status`, logout)
      ]);
      if (healthRes.ok) setHealth(await healthRes.json());
      if (schedulerRes.ok) setSchedulerStatus(await schedulerRes.json());
    } catch (err) {
      if (err.message !== 'Session expired') console.error('Error fetching system data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString();
  };

  if (loading || !health) {
    return <div className="text-center text-gray-500 py-8">Loading system health...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Database */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Database</h3>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            health.database.healthy ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {health.database.status}
          </div>
        </div>

        {/* Stripe */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Stripe</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Mode:</span>
              <span className={`font-medium ${health.stripe.mode === 'live' ? 'text-green-600' : 'text-yellow-600'}`}>
                {health.stripe.mode}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Webhooks:</span>
              <span className={`font-medium ${health.stripe.webhook_configured ? 'text-green-600' : 'text-red-600'}`}>
                {health.stripe.webhook_configured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
          </div>
        </div>

        {/* Scheduler Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Scheduler</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Jobs:</span>
              <span className="font-medium">{schedulerStatus?.jobs?.length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Recent Auto-Completes (24h):</span>
              <span className="font-medium">{health.scheduler.recent_auto_completes}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Recent Errors:</span>
              <span className={`font-medium ${(schedulerStatus?.recent_errors?.length || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {schedulerStatus?.recent_errors?.length || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduler Jobs Table */}
      {schedulerStatus?.jobs && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Scheduler Jobs</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedulerStatus.jobs.map(job => (
                  <tr key={job.id}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{job.name}</div>
                      <div className="text-xs text-gray-400">{job.id}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.interval || job.trigger}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(job.last_run)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(job.next_run)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        job.status === 'success' ? 'bg-green-100 text-green-800' :
                        job.status === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status || 'never run'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.duration_ms != null ? `${job.duration_ms}ms` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Errors Table */}
      {schedulerStatus?.recent_errors?.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden border-2 border-red-200">
          <div className="p-6 border-b bg-red-50">
            <h3 className="text-lg font-semibold text-red-800">Recent Scheduler Errors</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Message</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedulerStatus.recent_errors.map((err, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(err.timestamp)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {err.job_id}
                    </td>
                    <td className="px-4 py-4 text-sm text-red-600 max-w-md truncate" title={err.error}>
                      {err.error}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="text-center">
        <button
          onClick={fetchAll}
          className="bg-indigo-600 text-white rounded px-6 py-2 hover:bg-indigo-700"
        >
          Refresh Status
        </button>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, color }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="text-sm text-gray-500">{title}</div>
    <div className={`text-3xl font-bold text-${color}-600`}>{value}</div>
  </div>
);

const PipelineStep = ({ label, count }) => (
  <div className="text-center p-4 bg-gray-50 rounded">
    <div className="text-2xl font-bold text-gray-700">{count}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

export default Dashboard;
